// src/components/forms/formComponents/UsernameInput.js
import { useEffect, useState } from 'react';
import { functions } from '../../../firebase-client';
import { httpsCallable } from 'firebase/functions';

const BANNED = [
  'admin','administrator','support','help','contact','info',
  'membernest','root','system','login','register','dashboard'
];
const checkUsernameAvailabilityFn = httpsCallable(functions, 'checkUsernameAvailability');

export default function UsernameInput({
  value,
  onChange,
  setAvailable,
  userId = null,
  initialValue = '',
  inputId = 'public-username',
  name = 'public-username',
  placeholder = 'e.g. alex-smith',
  describedBy,
}) {
  const [touched, setTouched]         = useState(false);
  const [checking, setChecking]       = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);

  // Only run checks after first user change
  useEffect(() => {
    if (!touched) return;

    const name = value.trim().toLowerCase();
    const orig = initialValue.trim().toLowerCase();

    // 1) Empty → clear state
    if (!name) {
      setChecking(false);
      setIsAvailable(null);
      setAvailable(false);
      return;
    }

    // 2) Unchanged → immediately available
    if (name === orig) {
      setChecking(false);
      setIsAvailable(true);
      setAvailable(true);
      return;
    }

    // 3) Banned → immediately unavailable
    if (BANNED.includes(name)) {
      setChecking(false);
      setIsAvailable(false);
      setAvailable(false);
      return;
    }

    // 4) Otherwise debounce a Firestore check
    setChecking(true);
    setIsAvailable(null);

    const handler = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailabilityFn({
          username: name,
          excludeUserId: userId || '',
        });
        const ok = result?.data?.available === true;
        setIsAvailable(ok);
        setAvailable(ok);
      } catch {
        setIsAvailable(false);
        setAvailable(false);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [touched, value, initialValue, setAvailable, userId]);

  // Handler that marks the field as “touched”
  const handleChange = (e) => {
    const v = e.target.value.toLowerCase();
    if (!touched) setTouched(true);
    onChange(v);
  };

  // Choose an icon only after touched
  let icon = null;
  if (touched) {
    if (checking)           icon = '⏳';
    else if (isAvailable)   icon = '✅';
    else if (isAvailable===false) icon = '❌';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <input
        id={inputId}
        type="text"
        name={name}
        autoComplete="nickname"
        autoCapitalize="none"
        spellCheck={false}
        placeholder={placeholder}
        aria-describedby={describedBy}
        value={value}
        onChange={handleChange}
        required
        className="form-input"
        style={{ flex: 1 }}
      />
      {icon}
    </div>
  );
}
